import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Star } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import SectionDivider from "../components/SectionDivider";
import ProductCard from "../components/ProductCard";
import { fetchProducts } from "../lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_garnacha-nordic/artifacts/548npw6z_Sin%20t%C3%ADtulo%20%284500%20x%205100%20px%29.svg";
const HERO_IMG = "https://customer-assets.emergentagent.com/job_garnacha-nordic/artifacts/gh1jwgom_ChatGPT%20Image%2030%20jun%202026%2C%2010_52_48.png";

// Cinematic look — applied ONLY to the hero banner with the logo. Lightened slightly.
const HERO_CINEMATIC = "grayscale(0.15) brightness(0.7) contrast(1.1) saturate(1.05)";

const FEATURED_IDS = [
  "salsa-verde-habanero",
  "salsa-verde-taquera",
  "salsa-chipotle-taquera",
  "salsa-mango-habanero",
];

const Home = () => {
  const { t } = useLang();
  const [products, setProducts] = useState([]);

  useEffect(() => { fetchProducts().then(setProducts).catch(() => {}); }, []);

  const featured = FEATURED_IDS
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean);

  const why = [
    { t: t.why.f1t, b: t.why.f1b },
    { t: t.why.f2t, b: t.why.f2b },
    { t: t.why.f3t, b: t.why.f3b },
    { t: t.why.f4t, b: t.why.f4b },
    { t: t.why.f5t, b: t.why.f5b },
    { t: t.why.f6t, b: t.why.f6b },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO — only photo with cinematic treatment */}
      <section className="relative h-screen min-h-[760px] w-full overflow-hidden" data-testid="hero-section">
        <img
          src={HERO_IMG}
          alt="Authentic Mexican street food"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: HERO_CINEMATIC }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/45" />

        <div className="relative h-full flex flex-col items-center justify-center text-center container-pad pt-24 pb-20">
          <div className="animate-fade-up flex flex-col items-center">
            <img
              src={LOGO_URL}
              alt="GARNACHA"
              className="w-auto h-72 sm:h-96 lg:h-[34rem] xl:h-[38rem] object-contain drop-shadow-[0_8px_40px_rgba(0,0,0,0.5)]"
              data-testid="hero-logo"
            />

            <h1 className="display-serif text-[#FFF8F0] mt-10 text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight max-w-3xl drop-shadow-[0_2px_20px_rgba(0,0,0,0.55)]">
              {t.hero.title1} <span className="italic">{t.hero.title2}</span>
            </h1>

            <p className="mt-6 max-w-2xl text-[#FFF8F0]/90 text-base sm:text-lg font-light leading-relaxed">
              {t.hero.subtitle}
            </p>

            <SectionDivider variant="mark" className="mt-10 opacity-80" />

            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/b2b" data-testid="hero-cta-partner" className="btn-primary text-base px-10 py-4 shadow-[0_8px_24px_-8px_rgba(249,115,22,0.55)]">
                Become a Partner <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
              <Link to="/products" data-testid="hero-cta-products" className="btn-ghost-light">
                {t.common.exploreProducts}
              </Link>
            </div>
          </div>
          <div className="absolute bottom-8 left-0 right-0 flex justify-center text-[#FFF8F0]/45">
            <ChevronDown className="w-5 h-5 animate-bounce" />
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="border-t border-border" data-testid="featured-section">
        <SectionDivider variant="greca" className="pt-20 sm:pt-24" data-testid="divider-featured" />
        <div className="container-pad py-24 sm:py-32">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="eyebrow text-[#F97316] flex items-center justify-center gap-2">
              <Star className="w-3.5 h-3.5 fill-[#F97316]" /> {t.productsSection.featuredEyebrow}
            </div>
            <h2 className="display-serif mt-8 text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              Our Sauces.<br /><span className="italic">Made to be shared.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {featured.map((p) => <ProductCard key={p.id} product={p} flagship={p.flagship} />)}
          </div>

          <div className="mt-20 text-center">
            <Link to="/products" data-testid="featured-view-all" className="btn-outline inline-flex items-center">
              {t.common.viewAll} <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY GARNACHA — 6 points */}
      <section data-testid="why-section">
        <SectionDivider variant="greca" className="pt-20 sm:pt-24" data-testid="divider-why" />
        <div className="container-pad py-24 sm:py-32">
          <div className="max-w-3xl mb-20">
            <div className="eyebrow text-[#F97316]">{t.why.eyebrow}</div>
            <h2 className="display-serif mt-8 text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">{t.why.title}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-16">
            {why.map((w, i) => (
              <div key={i} data-testid={`why-feature-${i}`}>
                <h3 className="display-serif text-2xl sm:text-3xl leading-tight">{w.t}</h3>
                <div className="mt-5 w-10 h-px bg-[#F97316]" />
                <p className="mt-5 text-foreground/65 font-light leading-relaxed text-base">{w.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHORT BRAND STORY */}
      <section className="border-t border-border" data-testid="brand-story-section">
        <div className="container-pad py-32 sm:py-40">
          <div className="grid lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <div className="eyebrow text-[#F97316]">{t.discover.eyebrow}</div>
              <h2 className="display-serif mt-8 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] whitespace-pre-line">
                {t.discover.title}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-xl sm:text-2xl font-light leading-[1.6] text-foreground/75">
              <p>In Mexico, <em>garnacha</em> is a slang term used to describe street food, authentic, flavorful, and made to be shared.</p>
              <p>Inspired by that spirit and crafted in Finland, Garnacha brings genuine Mexican flavors through carefully selected ingredients, traditional recipes, and modern production standards.</p>
              <Link
                to="/our-history"
                data-testid="brand-story-cta"
                className="mt-10 inline-flex items-center btn-outline"
              >
                {t.common.learnMore} <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* CLOSING IMAGE — drippy tacos */}
      <section className="relative aspect-[16/8] sm:aspect-[16/7] w-full overflow-hidden" data-testid="closing-image">
        <img
          src="https://images.unsplash.com/photo-1599974579688-8dbdd335c77f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
          alt="Garnacha tacos"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </section>
    </div>
  );
};

export default Home;
